import { useState } from 'react';
import {
  Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody, Chip, Button, Typography, Box,
  Dialog, DialogTitle, DialogContent, DialogActions, Stack, Alert, Snackbar, TextField, Divider, Link,
} from '@mui/material';
import { Add, ArrowBack } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { ListFilters, matchFilters, EMPTY_FILTERS, type FilterValue } from '../../../components/ListFilters';
import { dealerApplicationsApi, type DealerApplication } from '../../../api/dealerApplications.api';
import { clientsApi } from '../../../api/clients.api';
import { dealerInsightsApi } from '../../../api/dealerInsights.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';
import { DealerRegistrationForm } from './DealerRegistrationForm';

const statusColor = (s: string) => (s === 'approved' ? 'success' : s === 'rejected' ? 'error' : s === 'verified' ? 'info' : 'warning');

export function DealerApplicationsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [creating, setCreating] = useState(false);
  const [filters, setFilters] = useState<FilterValue>({ ...EMPTY_FILTERS });
  const [detailId, setDetailId] = useState<number | null>(null);
  const [rejectFor, setRejectFor] = useState<DealerApplication | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['dealer-apps'], queryFn: () => dealerApplicationsApi.list({ limit: 200 }) });
  const clients = useQuery({ queryKey: ['clients', 'all'], queryFn: () => clientsApi.list({ limit: 500 }) });
  const ownership = useQuery({ queryKey: ['dealer-ownership'], queryFn: () => dealerInsightsApi.ownership() });
  const detail = useQuery({ queryKey: ['dealer-app', detailId], queryFn: () => dealerApplicationsApi.get(detailId!), enabled: detailId != null });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['dealer-apps'] });

  const clientOpts = (clients.data?.data ?? []).map((c: { id: number; clientName?: string; client_name?: string }) => ({ id: c.id, label: c.clientName ?? c.client_name ?? `Client #${c.id}` }));
  const teamOpts = (ownership.data?.teamMembers ?? []).map((m) => ({ id: m.userId, label: m.fullName }));

  const createMut = useMutation({
    mutationFn: (payload: Record<string, unknown>) => dealerApplicationsApi.create(payload),
    onSuccess: () => { setCreating(false); setError(null); setToast('Application created'); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const verifyMut = useMutation({ mutationFn: (id: number) => dealerApplicationsApi.verify(id), onSuccess: () => { setToast('Verified'); invalidate(); qc.invalidateQueries({ queryKey: ['dealer-app'] }); }, onError: (e) => setToast(extractApiError(e)) });
  const approveMut = useMutation({ mutationFn: (id: number) => dealerApplicationsApi.approve(id), onSuccess: () => { setToast('Approved — dealer created'); invalidate(); qc.invalidateQueries({ queryKey: ['dealer-app'] }); }, onError: (e) => setToast(extractApiError(e)) });
  const rejectMut = useMutation({ mutationFn: () => dealerApplicationsApi.reject(rejectFor!.id, reason), onSuccess: () => { setRejectFor(null); setReason(''); setToast('Rejected'); invalidate(); qc.invalidateQueries({ queryKey: ['dealer-app'] }); }, onError: (e) => setToast(extractApiError(e)) });

  if (creating) {
    return (
      <>
        <PageHeader title="New Dealer Application" subtitle="PDI Partner / Dealer onboarding."
          action={<Button startIcon={<ArrowBack />} onClick={() => setCreating(false)}>Back to list</Button>} />
        <DealerRegistrationForm clients={clientOpts} teamMembers={teamOpts} submitting={createMut.isPending} error={error} submitLabel="Create Application" onSubmit={(p) => createMut.mutate(p)} />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Dealer Applications" subtitle="PDI partner/dealer onboarding — verify → approve."
        action={hasPermission('dealer_application.create') ? <Button startIcon={<Add />} variant="contained" onClick={() => { setError(null); setCreating(true); }}>New Application</Button> : undefined} />

      <ListFilters value={filters} onChange={setFilters} statusOptions={['draft', 'submitted', 'verified', 'approved', 'rejected']} show={['search', 'status']} />

      <Card variant="outlined"><CardContent>
        <Table size="small">
          <TableHead><TableRow><TableCell>Application</TableCell><TableCell>Company</TableCell><TableCell>Source</TableCell><TableCell>Payment</TableCell><TableCell>Status</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {(list.data?.data ?? []).filter((a) => matchFilters(filters, { text: `${a.companyName} ${a.applicationCode} ${a.brandName ?? ''}`, status: a.status })).map((a) => (
              <TableRow key={a.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailId(a.id)}>
                <TableCell>{a.applicationCode}</TableCell>
                <TableCell><Typography fontWeight={600}>{a.companyName}</Typography>{a.brandName && <Typography variant="caption" color="text.secondary">{a.brandName}</Typography>}</TableCell>
                <TableCell><Chip size="small" variant="outlined" label={a.registrationSource} /></TableCell>
                <TableCell>{a.paymentTerms}{a.paymentTerms === 'credit' && a.creditDays ? ` (${a.creditDays}d)` : ''}</TableCell>
                <TableCell><Chip size="small" color={statusColor(a.status)} label={a.status} /></TableCell>
                <TableCell align="right"><Button size="small" onClick={(e) => { e.stopPropagation(); setDetailId(a.id); }}>Review</Button></TableCell>
              </TableRow>
            ))}
            {(list.data?.data.length ?? 0) === 0 && <TableRow><TableCell colSpan={6}><Box py={2}><Typography variant="body2" color="text.secondary" textAlign="center">No applications yet.</Typography></Box></TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Review dialog */}
      <Dialog open={detailId != null} onClose={() => setDetailId(null)} fullWidth maxWidth="md">
        <DialogTitle>{detail.data?.applicationCode} — {detail.data?.companyName}</DialogTitle>
        <DialogContent dividers>
          {detail.data && (
            <Stack spacing={1}>
              <Row k="Status" v={<Chip size="small" color={statusColor(detail.data.status)} label={detail.data.status} />} />
              <Row k="Dealer Type" v={detail.data.dealerType} />
              <Row k="Brand / Year" v={`${detail.data.brandName ?? '—'} / ${detail.data.establishmentYear ?? '—'}`} />
              <Row k="Contact" v={`${detail.data.primaryContactPerson ?? '—'} (${detail.data.designation ?? '—'}) · ${detail.data.mobile ?? '—'} · ${detail.data.email ?? '—'}`} />
              <Row k="Office" v={`${detail.data.billingAddress ?? '—'}, ${detail.data.city ?? ''} ${detail.data.district ?? ''} ${detail.data.pincode ?? ''}`} />
              <Row k="Finance" v={`${detail.data.financeContactPerson ?? '—'} · ${detail.data.financeEmail ?? '—'} · ${detail.data.paymentTerms}${detail.data.creditDays ? ` ${detail.data.creditDays}d` : ''}`} />
              <Row k="GST / PAN" v={`${detail.data.gstNumber ?? '—'} / ${detail.data.panNumber ?? '—'}`} />
              <Row k="Login" v={detail.data.loginEmail ?? '—'} />
              {detail.data.dealerId && <Row k="Dealer created" v={`#${detail.data.dealerId}`} />}
              {detail.data.rejectionReason && <Alert severity="error">Rejected: {detail.data.rejectionReason}</Alert>}
              <Divider />
              <Typography variant="subtitle2">Documents</Typography>
              {(detail.data.documents ?? []).length === 0 ? <Typography variant="caption" color="text.secondary">None</Typography> :
                (detail.data.documents ?? []).map((d) => <Typography key={d.id} variant="body2">{d.docType}: <Link href={d.fileUrl} target="_blank" rel="noopener">{d.fileName}</Link></Typography>)}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {detail.data?.status === 'submitted' && hasPermission('dealer_application.verify') && <Button color="info" onClick={() => verifyMut.mutate(detail.data!.id)}>Verify</Button>}
          {detail.data?.status === 'verified' && hasPermission('dealer_application.approve') && <Button color="success" variant="contained" onClick={() => approveMut.mutate(detail.data!.id)}>Approve → Create Dealer</Button>}
          {detail.data && !['approved', 'rejected'].includes(detail.data.status) && hasPermission('dealer_application.verify') && <Button color="error" onClick={() => { setRejectFor(detail.data!); setDetailId(null); }}>Reject</Button>}
          <Button onClick={() => setDetailId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectFor != null} onClose={() => setRejectFor(null)} fullWidth maxWidth="xs">
        <DialogTitle>Reject — {rejectFor?.applicationCode}</DialogTitle>
        <DialogContent><TextField label="Reason" fullWidth multiline minRows={2} value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mt: 1 }} /></DialogContent>
        <DialogActions><Button onClick={() => setRejectFor(null)}>Cancel</Button><Button color="error" variant="contained" disabled={reason.trim().length < 3 || rejectMut.isPending} onClick={() => rejectMut.mutate()}>Reject</Button></DialogActions>
      </Dialog>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return <Stack direction="row" spacing={2}><Typography variant="body2" sx={{ width: 140, color: 'text.secondary', flexShrink: 0 }}>{k}</Typography><Typography variant="body2" component="div">{v}</Typography></Stack>;
}
